import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper
} from '@mui/material';
import { CodeIcon } from '../icons';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';

/**
 * CreateDialog - Modal dialog for creating new projects/apps
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onClose - Function to call when dialog should close
 * @param {function} props.onSelectOption - Function called when an option is selected
 */
const CreateDialog = ({
  open,
  onClose,
  onSelectOption
}) => {
  // Options for creation
  const createOptions = [
    {
      id: 'project',
      title: 'Project',
      icon: <CodeIcon sx={{ fontSize: 28, color: '#485869' }} />,
      onClick: () => onSelectOption && onSelectOption('project')
    },
    {
      id: 'webapp',
      title: 'Web App',
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 28, color: '#485869' }} />,
      onClick: () => onSelectOption && onSelectOption('webapp')
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          margin: 'auto',
          maxHeight: '90vh',
          height: '90%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Dialog Title with Close Button */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          pl: 5,
          pr: 5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
          Create
        </Typography>
        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          size="small"
          sx={{
            color: '#9e9e9e',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#666'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ p: 3, pl: 5, pr: 5 }}>
        <Grid container spacing={2}>
          {createOptions.map((option) => (
            <Grid item xs={6} key={option.id}>
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  height: '130px',
                  bgcolor: '#f5f5f7',
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#eaeaec',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={option.onClick}
              >
                <Box 
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  {option.icon}
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 500,
                    color: '#333',
                    textAlign: 'center'
                  }}
                >
                  {option.title}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDialog;